import { useEffect, useRef, useState } from "react";
import { Share2, Download } from "lucide-react";
import { api } from "@/lib/api";
import { getAdminToken } from "@/lib/adminAuth";

const hdr = () => ({ headers: { Authorization: `Bearer ${getAdminToken()}` } });

/* 🎮 game-aware art — every game (and Pro Trade) gets its own emoji + clean name */
const GAME_ART = [
  ["penalty", "⚽"], ["party", "⚽"], ["dice", "🎲"], ["green", "🚦"], ["tower", "🧗"],
  ["gem", "💎"], ["lucky", "🔮"], ["mine", "🔮"], ["digit hunter", "🎯"], ["jackpot", "🎰"],
  ["slot", "🎰"], ["streak", "🔥"], ["boxing", "🥊"], ["red", "🃏"], ["black", "🃏"],
  ["hilo", "🎴"], ["high low", "📊"], ["rocket", "🚀"], ["heli", "🚁"], ["ride", "🎰"],
  ["car race", "🏎"], ["basketball", "🏀"],
  ["auto-trade", "🤖"], ["pro_trade", "📈"], ["pro trade", "📈"], ["manual", "📈"], ["mult", "📈"],
];
const gameArt = (win) => {
  const n = `${win.strategy || ""} ${win.source || ""}`.toLowerCase();
  const hit = GAME_ART.find(([k]) => n.includes(k));
  return hit ? hit[1] : "🏆";
};
const gameName = (win) => (win.strategy || win.source || "VTS WIN").replace(/^Arcade /i, "").replace(/_/g, " ").toUpperCase();

/* 🎴 Win Card Studio — turns a real user win into a 1080×1350 branded card
   the owner can download and post on Instagram/TikTok/Telegram. */
const drawCard = (canvas, win) => {
  const W = 1080, H = 1350;
  canvas.width = W; canvas.height = H;
  const x = canvas.getContext("2d");
  const bg = x.createLinearGradient(0, 0, 0, H);
  bg.addColorStop(0, "#0B0E14"); bg.addColorStop(0.55, "#101522"); bg.addColorStop(1, "#071a14");
  x.fillStyle = bg; x.fillRect(0, 0, W, H);
  x.strokeStyle = "rgba(8,153,129,0.5)"; x.lineWidth = 6; x.strokeRect(24, 24, W - 48, H - 48);
  x.fillStyle = "#2f6bff"; x.font = "900 64px Arial"; x.textAlign = "center";
  x.fillText("VTS", W / 2, 150);
  x.fillStyle = "rgba(255,255,255,0.55)"; x.font = "700 34px Arial";
  x.fillText(win.source === "pro_trade" ? "PRO TRADE" : "TRADING ARCADE", W / 2, 200);
  x.font = "140px Arial"; x.fillText(gameArt(win), W / 2, 400);
  x.fillStyle = "#ffffff"; x.font = "900 58px Arial";
  x.fillText(gameName(win), W / 2, 500);
  x.fillStyle = "rgba(255,255,255,0.45)"; x.font = "600 36px Arial";
  x.fillText(`${win.symbol || ""} · real Deriv contract`, W / 2, 560);
  x.fillStyle = "#0fd9a0"; x.font = "900 170px Arial";
  x.fillText(`+$${Number(win.profit || 0).toFixed(2)}`, W / 2, 780);
  x.fillStyle = "rgba(255,255,255,0.6)"; x.font = "700 42px Arial";
  x.fillText(`from a $${Number(win.stake || 0).toFixed(2)} stake`, W / 2, 860);
  x.fillStyle = "rgba(255,255,255,0.35)"; x.font = "600 32px Arial";
  x.fillText(`player ${win.acct_masked || "***"} · ${(win.settled_at || "").slice(0, 10)}`, W / 2, 940);
  x.fillStyle = "#F7B731"; x.font = "900 52px Arial";
  x.fillText("PLAY WITH A FREE $10K DEMO", W / 2, 1120);
  x.fillStyle = "#ffffff"; x.font = "900 60px Arial";
  x.fillText("vtstradingbot.com", W / 2, 1200);
  x.fillStyle = "rgba(255,255,255,0.3)"; x.font = "500 26px Arial";
  x.fillText("Real trading carries risk — trade what you can afford to lose", W / 2, 1280);
};

const WinCardStudio = () => {
  const [wins, setWins] = useState(null);
  const [sel, setSel] = useState(0);
  const canvasRef = useRef(null);
  useEffect(() => {
    api.get("/admin/marketing/wins", hdr()).then(({ data }) => setWins(data.wins || [])).catch(() => setWins([]));
  }, []);
  useEffect(() => {
    if (wins && wins[sel] && canvasRef.current) drawCard(canvasRef.current, wins[sel]);
  }, [wins, sel]);
  const download = async () => {
    // 📱 mobile-safe: blob + native share sheet first (data-URL anchors are
    // silently blocked in many mobile/in-app browsers)
    const blob = await new Promise((r) => canvasRef.current.toBlob(r, "image/png"));
    if (!blob) return;
    const file = new File([blob], `vts-win-${sel + 1}.png`, { type: "image/png" });
    if (navigator.canShare && navigator.canShare({ files: [file] })) {
      try { await navigator.share({ files: [file], title: "VTS win" }); return; } catch { /* user cancelled → fall through */ }
    }
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `vts-win-${sel + 1}.png`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 4000);
  };
  if (!wins) return null;
  return (
    <div data-testid="win-card-studio" className="rounded-xl border border-white/10 bg-[#0B0E14] p-4">
      <div className="flex items-center gap-2 mb-3">
        <Share2 className="w-4 h-4 text-[#0fd9a0]" />
        <h3 className="text-sm font-black text-white">Win Card Studio — post real wins</h3>
        <span className="ml-auto text-[9px] font-mono text-white/35">1080×1350 · Instagram ready</span>
      </div>
      {wins.length === 0 ? (
        <p className="text-[10px] font-mono text-white/35">no real-money wins recorded yet — cards appear as soon as players win</p>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="max-h-[320px] overflow-y-auto space-y-1 pr-1">
            {wins.map((w, i) => (
              <button key={i} data-testid={`win-card-pick-${i}`} onClick={() => setSel(i)}
                className={`w-full text-left rounded-md border px-3 py-2 text-[10px] font-mono transition-colors ${i === sel ? "border-[#0fd9a0]/60 bg-[#0fd9a0]/10" : "border-white/10 hover:bg-white/5"}`}>
                <span className="font-bold text-[#0fd9a0]">+${Number(w.profit || 0).toFixed(2)}</span>
                <span className="text-white/60"> · {gameArt(w)} {gameName(w)} · {w.symbol}</span>
                <span className="text-white/30"> · {(w.settled_at || "").slice(0, 10)}</span>
              </button>
            ))}
          </div>
          <div>
            <canvas ref={canvasRef} className="w-full rounded-lg border border-white/10" style={{ maxWidth: 280 }} />
            <button data-testid="win-card-download" onClick={download}
              className="mt-2 flex items-center gap-2 rounded-lg bg-[#0fd9a0] px-4 py-2 text-xs font-black text-black hover:bg-[#12eab0] transition-colors">
              <Download className="w-3.5 h-3.5" /> Save / Share PNG
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default WinCardStudio;
